import React, { useCallback, useEffect, useState } from 'react';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';

import apiClient from '../services/api';
import { avisarConsultasActualizadas } from '../hooks/useConsultasPendientes';

// 17 sep 2026: panel de Augusto para la agenda propia que reemplaza a
// Cal.com (ver components/ConsultaScheduler.js y el backend en
// endpoints/consultations.py). Dos cosas: aprobar o rechazar las
// solicitudes que llegan, y editar el horario de atención -- esto último
// es la pieza que en ACS resultó indispensable, porque tener el horario
// fijo en el código significaba que cambiar un sábado exigía desplegar.
//
// Pantalla de admin: va solo en español, igual que ClientesPage.js y
// TestersPage.js. La ve una sola persona.

const DIAS = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

const COLOR_ESTADO = {
  pendiente: 'warning',
  confirmada: 'success',
  rechazada: 'default',
  cancelada: 'default',
};

const ConsultasPage = () => {
  const [tab, setTab] = useState('solicitudes');

  const [solicitudes, setSolicitudes] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  const [horario, setHorario] = useState([]);
  const [guardandoHorario, setGuardandoHorario] = useState(false);
  const [avisoHorario, setAvisoHorario] = useState(null);

  const [google, setGoogle] = useState(null);
  const [conectando, setConectando] = useState(false);

  const [resolviendo, setResolviendo] = useState(null); // { solicitud, accion }
  const [nota, setNota] = useState('');
  const [enviandoResolucion, setEnviandoResolucion] = useState(false);

  const cargar = useCallback(() => {
    setCargando(true);
    Promise.all([
      apiClient.get('/consultations/admin/requests'),
      apiClient.get('/consultations/admin/schedule'),
      // El estado de Google no debe tumbar la pantalla si falla: la
      // agenda funciona sin él.
      apiClient.get('/consultations/admin/google/status').catch(() => null),
    ])
      .then(([resSol, resHor, resGoogle]) => {
        setSolicitudes(resSol.data?.solicitudes || []);
        setHorario(resHor.data?.dias || []);
        setGoogle(resGoogle?.data || null);
        setError(null);
      })
      .catch((err) => {
        setError(
          err?.response?.status === 403
            ? 'Esta pantalla es solo para el administrador.'
            : 'No se pudo cargar la agenda.'
        );
      })
      .finally(() => setCargando(false));
  }, []);

  useEffect(cargar, [cargar]);

  const abrirResolucion = (solicitud, accion) => {
    setResolviendo({ solicitud, accion });
    setNota(
      accion === 'confirm'
        ? 'Nos vemos por Google Meet, te paso el enlace por correo.'
        : 'Ese horario se me cruzó. ¿Te va otro día de esa misma semana?'
    );
  };

  const resolver = async () => {
    if (!resolviendo) return;
    setEnviandoResolucion(true);
    try {
      await apiClient.post(
        `/consultations/admin/requests/${resolviendo.solicitud.id}/${resolviendo.accion}`,
        { nota: nota.trim() || null }
      );
      setResolviendo(null);
      cargar();
      // Baja el contador rojo del header y de la barra lateral ahora mismo.
      avisarConsultasActualizadas();
    } catch (err) {
      setError('No se pudo guardar la respuesta.');
    } finally {
      setEnviandoResolucion(false);
    }
  };

  // Abre la pantalla de permisos de Google en una pestaña nueva. La URL
  // se pide al backend (y no se arma acá) porque lleva el client_id y el
  // redirect_uri exactos que tiene cargados el servidor -- armarla en el
  // front sería una segunda copia que se desincroniza.
  const conectarGoogle = async () => {
    setConectando(true);
    try {
      const res = await apiClient.get('/consultations/admin/google/start');
      window.open(res.data.url, '_blank', 'noopener');
    } catch (err) {
      setError(err?.response?.data?.detail || 'No se pudo iniciar la autorización de Google.');
    } finally {
      setConectando(false);
    }
  };

  const cambiarDia = (idx, campo, valor) => {
    setHorario((prev) => prev.map((d, i) => (i === idx ? { ...d, [campo]: valor } : d)));
  };

  const guardarHorario = async () => {
    setGuardandoHorario(true);
    setAvisoHorario(null);
    try {
      await apiClient.put('/consultations/admin/schedule', { dias: horario });
      setAvisoHorario({ tipo: 'success', texto: 'Horario guardado.' });
    } catch (err) {
      setAvisoHorario({
        tipo: 'error',
        texto: err?.response?.data?.detail || 'No se pudo guardar el horario.',
      });
    } finally {
      setGuardandoHorario(false);
    }
  };

  const pendientes = solicitudes.filter((s) => s.estado === 'pendiente');
  const resueltas = solicitudes.filter((s) => s.estado !== 'pendiente');

  const renderSolicitud = (s) => (
    <Paper key={s.id} variant="outlined" sx={{ p: 2, mb: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>
            {s.fecha} · {s.hora} <Typography component="span" variant="body2" color="text.secondary">(hora de Lima, {s.duracion_min} min)</Typography>
          </Typography>
          <Typography variant="body2">
            {s.nombre} — {s.email}
            {s.telefono ? ` — ${s.telefono}` : ''}
            {s.idioma === 'en' ? ' — escribe en inglés' : ''}
          </Typography>
          {s.mensaje && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
              "{s.mensaje}"
            </Typography>
          )}
          {s.nota_admin && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Tu nota: {s.nota_admin}
            </Typography>
          )}
          {s.meet_link && (
            <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
              Meet:{' '}
              <a href={s.meet_link} target="_blank" rel="noopener noreferrer">
                {s.meet_link}
              </a>
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Chip size="small" label={s.estado} color={COLOR_ESTADO[s.estado] || 'default'} />
          {s.estado === 'pendiente' && (
            <>
              <Button size="small" variant="contained" onClick={() => abrirResolucion(s, 'confirm')}>
                Confirmar
              </Button>
              <Button size="small" color="error" onClick={() => abrirResolucion(s, 'reject')}>
                Rechazar
              </Button>
            </>
          )}
        </Box>
      </Box>
    </Paper>
  );

  return (
    <Container maxWidth="lg" sx={{ pt: 4, pb: 8 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Consultas
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Las solicitudes que llegan desde la página pública. Ninguna cita queda en firme hasta que
        la confirmas acá — al hacerlo, le llega un correo al cliente.
      </Typography>

      {cargando && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && !cargando && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!cargando && !error && (
        <>
          <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
            <Tab value="solicitudes" label={`Solicitudes${pendientes.length ? ` (${pendientes.length})` : ''}`} />
            <Tab value="horario" label="Horario de atención" />
            <Tab value="google" label="Google Calendar" />
          </Tabs>

          {tab === 'solicitudes' && (
            <>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Pendientes de tu respuesta
              </Typography>
              {pendientes.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Nada pendiente.
                </Typography>
              ) : (
                pendientes.map(renderSolicitud)
              )}

              {resueltas.length > 0 && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Ya resueltas
                  </Typography>
                  {resueltas.map(renderSolicitud)}
                </>
              )}
            </>
          )}

          {tab === 'horario' && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Los horarios que se le ofrecen a un visitante salen de acá. Se muestran candidatos
                cada 30 minutos, y cada consulta reserva 60 minutos — el último horario de cada día
                es el que alcanza a terminar antes del cierre. Un día desactivado no aparece en el
                calendario público.
              </Typography>

              {horario.map((d, idx) => (
                <Grid container spacing={2} alignItems="center" key={d.dia_semana} sx={{ mb: 1 }}>
                  <Grid item xs={12} sm={4}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={d.activo}
                          onChange={(e) => cambiarDia(idx, 'activo', e.target.checked)}
                        />
                      }
                      label={DIAS[d.dia_semana]}
                    />
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <TextField
                      fullWidth size="small" type="time" label="Desde" disabled={!d.activo}
                      value={d.desde}
                      onChange={(e) => cambiarDia(idx, 'desde', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <TextField
                      fullWidth size="small" type="time" label="Hasta" disabled={!d.activo}
                      value={d.hasta}
                      onChange={(e) => cambiarDia(idx, 'hasta', e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>
              ))}

              {avisoHorario && (
                <Alert severity={avisoHorario.tipo} sx={{ mt: 2 }}>
                  {avisoHorario.texto}
                </Alert>
              )}

              <Button
                variant="contained"
                sx={{ mt: 2 }}
                onClick={guardarHorario}
                disabled={guardandoHorario}
              >
                {guardandoHorario ? 'Guardando...' : 'Guardar horario'}
              </Button>
            </Paper>
          )}

          {tab === 'google' && (
            <Paper variant="outlined" sx={{ p: 2 }}>
              {!google && (
                <Alert severity="warning">
                  No se pudo leer el estado de la integración.
                </Alert>
              )}

              {google && (
                <>
                  <Alert severity={google.configurado ? 'success' : 'info'} sx={{ mb: 2 }}>
                    {google.configurado
                      ? 'Conectado. Los horarios ocupados de tu calendario ya no se ofrecen, y al confirmar una consulta se crea el evento con Google Meet.'
                      : 'Sin conectar. La agenda funciona igual, pero solo sabe de las consultas reservadas en PCM — puede ofrecer un horario donde ya tengas otra cosa.'}
                  </Alert>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Credenciales en el servidor: {google.tiene_credenciales ? 'sí' : 'faltan'}.
                    {' '}Permiso de tu cuenta: {google.tiene_refresh_token ? 'sí' : 'falta'}.
                    {' '}Calendario: <code>{google.calendar_id}</code>.
                  </Typography>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Esta es la URL de retorno que tiene que estar cargada en Google Cloud,
                    exactamente igual:
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: 'action.hover' }}>
                    <Typography variant="caption" sx={{ wordBreak: 'break-all' }}>
                      {google.redirect_uri}
                    </Typography>
                  </Paper>

                  <Button
                    variant="contained"
                    onClick={conectarGoogle}
                    disabled={conectando || !google.tiene_credenciales}
                  >
                    {conectando ? 'Abriendo...' : google.tiene_refresh_token ? 'Volver a autorizar' : 'Autorizar mi Google Calendar'}
                  </Button>
                  {!google.tiene_credenciales && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      Primero hay que cargar GOOGLE_CALENDAR_CLIENT_ID y
                      GOOGLE_CALENDAR_CLIENT_SECRET en Render.
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Al autorizar, Google te devuelve un refresh token en pantalla. Cópialo a Render
                    como GOOGLE_CALENDAR_REFRESH_TOKEN y reinicia el servicio.
                  </Typography>
                </>
              )}
            </Paper>
          )}
        </>
      )}

      <Dialog open={Boolean(resolviendo)} onClose={() => setResolviendo(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {resolviendo?.accion === 'confirm' ? 'Confirmar la consulta' : 'Rechazar la consulta'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {resolviendo && (
              <>
                {resolviendo.solicitud.nombre} — {resolviendo.solicitud.fecha} a las{' '}
                {resolviendo.solicitud.hora}. Este texto va dentro del correo que recibe.
              </>
            )}
          </Typography>
          <TextField
            fullWidth multiline rows={3} autoFocus
            label="Nota para el cliente (opcional)"
            value={nota}
            onChange={(e) => setNota(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolviendo(null)} disabled={enviandoResolucion}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color={resolviendo?.accion === 'confirm' ? 'primary' : 'error'}
            onClick={resolver}
            disabled={enviandoResolucion}
          >
            {enviandoResolucion
              ? 'Enviando...'
              : resolviendo?.accion === 'confirm'
                ? 'Confirmar y avisar'
                : 'Rechazar y avisar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ConsultasPage;
